import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  getBallkids,
  getFreikartenFresh,
  updateFreikarten,
  confirmFreikarten
} from "../firebaseService";
import { useTournamentConfig } from "../context/TournamentConfigContext";
import { useGroup } from "../context/GroupContext";
import { formatDayWithWeekday } from "../utils/dateLabel";

// Native select for performance - MUI Select is too heavy for 450+ cells
const FreikartenCell = memo(({ ballkid, day, maxFreikarten, onChange, onConfirm }) => {
  const totalFreikarten = ballkid.freikarten.total || 0;
  const currentValue = ballkid.freikarten.days?.[day] || 0;
  const remainingFreikarten = maxFreikarten - totalFreikarten + currentValue;
  const confirmed = ballkid.freikarten.confirmed?.[day] || false;

  if (confirmed) {
    return (
      <TableCell align="center">
        <Typography variant="body2">&#10004; {currentValue} abgeholt</Typography>
      </TableCell>
    );
  }

  return (
    <TableCell align="center">
      <select
        value={currentValue}
        onChange={(e) => onChange(ballkid.id, day, parseInt(e.target.value))}
        disabled={remainingFreikarten <= 0 && currentValue === 0}
        style={{
          padding: "6px 8px",
          borderRadius: 4,
          border: "1px solid rgba(255,255,255,0.23)",
          backgroundColor: "transparent",
          color: "inherit",
          fontSize: "0.875rem",
          cursor: "pointer",
        }}
      >
        {[...Array(Math.max(remainingFreikarten + 1, 1)).keys()].map((num) => (
          <option key={num} value={num}>{num}</option>
        ))}
      </select>
      {currentValue > 0 && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => onConfirm(ballkid.id, day)}
          sx={{ mt: 0.5, display: "block", mx: "auto" }}
        >
          Abholen
        </Button>
      )}
    </TableCell>
  );
});

const FreikartenRow = memo(({ ballkid, daysOfWeek, maxFreikarten, onChange, onConfirm }) => (
  <TableRow>
    <TableCell
      sx={{
        position: "sticky",
        left: 0,
        backgroundColor: "#1a2138",
        zIndex: 1,
        whiteSpace: "nowrap",
        minWidth: 120,
      }}
    >
      {ballkid.name}
    </TableCell>
    {daysOfWeek.map((day) => (
      <FreikartenCell
        key={day}
        ballkid={ballkid}
        day={day}
        maxFreikarten={maxFreikarten}
        onChange={onChange}
        onConfirm={onConfirm}
      />
    ))}
  </TableRow>
));

const Freikarten = () => {
  const { daysOfWeek, maxFreikarten } = useTournamentConfig();
  const { activeGroup } = useGroup();
  const [ballkids, setBallkids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!activeGroup) return;
    setLoading(true);
    setError(null);
    const fetchData = async () => {
      try {
        const [allBallkids, allFreikarten] = await Promise.all([
          getBallkids(),
          getFreikartenFresh(),
        ]);

        const freikartenMap = {};
        allFreikarten.forEach((fk) => { freikartenMap[fk.id] = fk; });

        const combinedData = allBallkids.map((bk) => ({
          ...bk,
          freikarten: freikartenMap[bk.id] || { total: 0, days: {}, confirmed: {} },
        }));

        combinedData.sort((a, b) => a.name.localeCompare(b.name));
        setBallkids(combinedData);
      } catch (err) {
        console.error("Fehler beim Laden der Freikarten:", err);
        setError("Fehler beim Laden der Daten.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeGroup]);

  const handleFreikartenChange = useCallback(async (ballkidId, day, count) => {
    try {
      await updateFreikarten(ballkidId, day, count, maxFreikarten);
      setBallkids((prev) =>
        prev.map((bk) =>
          bk.id === ballkidId
            ? {
                ...bk,
                freikarten: {
                  ...bk.freikarten,
                  days: { ...(bk.freikarten.days || {}), [day]: count },
                  total: Object.values({
                    ...(bk.freikarten.days || {}),
                    [day]: count
                  }).reduce((sum, val) => sum + val, 0)
                }
              }
            : bk
        )
      );
    } catch (err) {
      console.error("Fehler beim Aktualisieren:", err);
      setError("Fehler beim Speichern der Freikarten.");
    }
  }, [maxFreikarten]);

  const handleConfirmFreikarten = useCallback(async (ballkidId, day) => {
    try {
      await confirmFreikarten(ballkidId, day);
      setBallkids((prev) =>
        prev.map((bk) =>
          bk.id === ballkidId
            ? {
                ...bk,
                freikarten: {
                  ...bk.freikarten,
                  confirmed: { ...(bk.freikarten.confirmed || {}), [day]: true }
                }
              }
            : bk
        )
      );
    } catch (err) {
      console.error("Fehler beim Bestaetigen:", err);
      setError("Fehler beim Bestaetigen der Abholung.");
    }
  }, []);

  const dailyTotals = daysOfWeek.reduce((totals, day) => {
    const total = ballkids.reduce(
      (sum, bk) => sum + (bk.freikarten?.days?.[day] || 0),
      0
    );
    return { ...totals, [day]: total };
  }, {});

  const filteredBallkids = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return ballkids;
    return ballkids.filter((bk) => (bk.name || "").toLowerCase().includes(term));
  }, [ballkids, searchTerm]);

  const handleExport = useCallback(async () => {
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Freikarten");

      const dayLabels = daysOfWeek.map(formatDayWithWeekday);
      const headerRow = ["Name", ...dayLabels, "Gesamt"];
      ws.addRow(headerRow);
      const headerRowRef = ws.getRow(1);
      headerRowRef.font = { bold: true };
      headerRowRef.alignment = { horizontal: "center" };

      ballkids.forEach((bk) => {
        const dayValues = daysOfWeek.map((day) => bk.freikarten?.days?.[day] || 0);
        const total = dayValues.reduce((sum, v) => sum + v, 0);
        ws.addRow([bk.name, ...dayValues, total]);
      });

      const dailySum = daysOfWeek.map((day) => dailyTotals[day] || 0);
      const grandTotal = dailySum.reduce((sum, v) => sum + v, 0);
      const totalsRow = ws.addRow(["Summe", ...dailySum, grandTotal]);
      totalsRow.font = { bold: true };
      totalsRow.eachCell((cell) => {
        cell.border = { top: { style: "thin" } };
      });

      const confirmedSum = daysOfWeek.map((day) =>
        ballkids.reduce(
          (sum, bk) =>
            sum + (bk.freikarten?.confirmed?.[day] ? (bk.freikarten?.days?.[day] || 0) : 0),
          0
        )
      );
      const confirmedGrand = confirmedSum.reduce((sum, v) => sum + v, 0);
      const confirmedRow = ws.addRow(["Davon abgeholt", ...confirmedSum, confirmedGrand]);
      confirmedRow.font = { italic: true };

      ws.columns = headerRow.map((h, i) => ({
        header: h,
        width: i === 0 ? 24 : Math.max(String(h).length + 2, 10),
      }));
      for (let i = 2; i <= headerRow.length; i++) {
        ws.getColumn(i).alignment = { horizontal: "center" };
      }

      const buffer = await wb.xlsx.writeBuffer();
      const groupName = activeGroup?.name || "Gruppe";
      const dateStr = new Date().toISOString().slice(0, 10);
      saveAs(new Blob([buffer]), `Freikarten_${groupName}_${dateStr}.xlsx`);
    } catch (err) {
      console.error("Fehler beim Export:", err);
      setError("Fehler beim Export der Freikarten-Liste.");
    }
  }, [ballkids, daysOfWeek, dailyTotals, activeGroup]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Typography variant="h4">Freikarten Verwaltung</Typography>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={ballkids.length === 0}
        >
          Liste exportieren
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ padding: 2, marginBottom: 2 }}>
        <Typography variant="h6">Freikarten Gesamtanzahl pro Tag</Typography>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {daysOfWeek.map((day) => (
                  <TableCell key={day} align="center" sx={{ whiteSpace: "nowrap" }}>
                    {formatDayWithWeekday(day)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {daysOfWeek.map((day) => (
                  <TableCell key={day} align="center">{dailyTotals[day]}</TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={`${activeGroup?.memberLabel || "Mitglieder"} durchsuchen...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {searchTerm && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            {filteredBallkids.length} von {ballkids.length} angezeigt
          </Typography>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: "90vh" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  top: 0,
                  left: 0,
                  backgroundColor: "#1a2138",
                  zIndex: 3,
                  whiteSpace: "nowrap",
                  minWidth: 120,
                }}
              >
                Name
              </TableCell>
              {daysOfWeek.map((day) => (
                <TableCell
                  key={day}
                  align="center"
                  sx={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#1a2138",
                    zIndex: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatDayWithWeekday(day)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBallkids.map((ballkid) => (
              <FreikartenRow
                key={ballkid.id}
                ballkid={ballkid}
                daysOfWeek={daysOfWeek}
                maxFreikarten={maxFreikarten}
                onChange={handleFreikartenChange}
                onConfirm={handleConfirmFreikarten}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Freikarten;
